import React from 'react';
import { Building2, MapPin, Plus, Trash2, Upload, Download, Pencil, AlertTriangle } from 'lucide-react';
import { CodeNAFAutocomplete } from '../CodeNAFAutocomplete';
import TranchesEligibilite from '../TranchesEligibilite';

/**
 * Renvoie le domaine de tension "suggéré" depuis la puissance souscrite (kVA).
 * Aligné sur la logique backend : <36 → bt_inf, 36..250 → bt_sup, >250 → hta.
 */
const suggestedTensionFromKva = (kva) => {
  const p = Number(kva);
  if (!p || isNaN(p)) return null;
  if (p < 36) return 'bt_inf';
  if (p <= 250) return 'bt_sup';
  return 'hta';
};

const TENSION_LABEL = { bt_inf: 'BT inférieure', bt_sup: 'BT supérieure', hta: 'HTA' };

/**
 * Étape 4 du formulaire TICFE multi-site : gestion des Sites (SIRET) et de leurs PDL.
 * Reçoit toutes les dépendances via un unique objet `ctx` pour limiter le drilling.
 */
export const Step4Sites = ({ ctx }) => {
  const {
    formData, setFormData, modeCalcul, setStep,
    sauvegarderBrouillon,
    showAddSite, setShowAddSite,
    showImportSites, setShowImportSites,
    architectureImportee, setArchitectureImportee,
    showRepartitionVAC, setShowRepartitionVAC,
    modeRepartition, setModeRepartition,
    repartitionManuelle, setRepartitionManuelle,
    currentSite, setCurrentSite,
    currentPDLMulti, setCurrentPDLMulti,
    editingPDLMultiIndex, setEditingPDLMultiIndex,
    handleImportSitesExcel, downloadTemplateSites,
    repartirVACAutomatique, validerArchitecture,
    addPDLToSite, addSiteToRapport,
  } = ctx;

  return (
    <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
      {/* Boutons navigation en haut */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#BFC6CC]">
        <button
          onClick={() => setStep(3)}
          className="px-4 py-2 border border-[#BFC6CC] text-[#687175] font-medium rounded-lg hover:bg-[#F5F6F9] text-sm"
        >
          ← Retour
        </button>
        <button
          type="button"
          onClick={sauvegarderBrouillon}
          className="px-4 py-2 border border-[#F9C80E] text-[#F9C80E] font-medium rounded-lg hover:bg-[#FFF9E6] text-sm"
        >
          💾 Enregistrer Brouillon
        </button>
      </div>

      <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
        Sites et Points De Livraison
      </h2>

      <p className="text-[#687175] mb-6">
        {modeCalcul === 'siren'
          ? 'Mode regroupé : Ajoutez tous les PDL de votre entreprise. Une seule VAC commune.'
          : 'Mode site par site : Créez des sites (SIRET) avec leurs PDL. Chaque site aura sa VAC.'}
      </p>

      {/* Liste sites déjà créés */}
      {formData.sites.length > 0 && (
        <div className="mb-6 space-y-4">
          {formData.sites.map((site, siteIndex) => (
            <div key={siteIndex} className="border border-[#BFC6CC] rounded-lg overflow-hidden">
              <div className="bg-[#F5F6F9] px-6 py-3 border-b border-[#BFC6CC] flex items-center justify-between">
                <div>
                  <span className="font-semibold text-[#191D1E]">Site {siteIndex + 1}</span>
                  {site.siret && <span className="ml-4 font-mono text-sm text-[#687175]">SIRET: {site.siret}</span>}
                  {modeCalcul === 'siret' && site.valeur_ajoutee_siret && (
                    <span className="ml-4 text-sm text-[#397DFF]">VAC: {parseFloat(site.valeur_ajoutee_siret).toLocaleString('fr-FR')} €</span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setFormData({
                      ...formData,
                      sites: formData.sites.filter((_, i) => i !== siteIndex)
                    });
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4">
                <div className="text-sm text-[#687175] mb-2">
                  {site.pdls?.length || 0} PDL
                  {site.taux_reduit_deja_applique && (
                    <span className="ml-3 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Taux réduit déjà appliqué</span>
                  )}
                </div>
                {site.pdls?.map((pdl, pdlIndex) => (
                  <div key={pdlIndex} className="text-sm text-[#191D1E] mb-1">
                    • {pdl.pdl} - {pdl.puissance_souscrite} kVA - {pdl.code_naf}
                    {pdl.domaine_tension_override && (
                      <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                        {{ bt_inf: 'BT inf.', bt_sup: 'BT sup.', hta: 'HTA' }[pdl.domaine_tension_override]} forcé
                      </span>
                    )}
                    {pdl.tranches_eligibilite && pdl.tranches_eligibilite.length > 0 && (
                      <span className="ml-2 text-xs">
                        {pdl.tranches_eligibilite.map((t, ti) => (
                          <span key={ti} className={`inline-block mr-1 px-1.5 py-0.5 rounded ${t.motif === 'EXONERATION' ? 'bg-emerald-100 text-emerald-700' : t.motif === 'FUITE_CARBONE' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                            {t.part}% {t.motif === 'EXONERATION' ? 'exo' : t.motif === 'FUITE_CARBONE' ? 'fuite C' : 'élec-int'}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Zone import ou ajout manuel */}
      {formData.sites.length === 0 && !showAddSite && !showImportSites ? (
        <div className="grid grid-cols-2 gap-6">
          {/* Import Excel */}
          <div className="p-8 bg-white border-2 border-[#BFC6CC] rounded-lg hover:border-[#397DFF] transition-all">
            <div className="flex flex-col items-center text-center gap-4">
              <Upload className="w-12 h-12 text-[#397DFF]" />
              <div>
                <h3 className="font-semibold text-[#191D1E] mb-2">Import Excel</h3>
                <p className="text-sm text-[#687175] mb-4">
                  Importer plusieurs sites et PDL en masse
                </p>
                <button
                  type="button"
                  onClick={downloadTemplateSites}
                  className="text-sm text-[#397DFF] hover:text-[#4672B6] mb-3"
                >
                  <Download className="w-4 h-4 inline mr-1" />
                  Télécharger template
                </button>
                <label className="block">
                  <span className="px-4 py-2 bg-[#397DFF] hover:bg-[#4672B6] text-white rounded-lg cursor-pointer inline-block text-sm">
                    Charger fichier Excel
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportSitesExcel}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Ajout manuel */}
          <div className="p-8 bg-white border-2 border-[#BFC6CC] rounded-lg hover:border-[#397DFF] transition-all">
            <div className="flex flex-col items-center text-center gap-4">
              <Plus className="w-12 h-12 text-[#687175]" />
              <div>
                <h3 className="font-semibold text-[#191D1E] mb-2">Ajout Manuel</h3>
                <p className="text-sm text-[#687175] mb-4">
                  Créer site par site avec formulaire
                </p>
                <button
                  type="button"
                  onClick={() => setShowAddSite(true)}
                  className="px-4 py-2 bg-[#397DFF] hover:bg-[#4672B6] text-white rounded-lg text-sm"
                >
                  Créer un Site
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Vue synthétique architecture importée */}
      {showImportSites && architectureImportee && (
        <div className="bg-white border-2 border-[#397DFF] rounded-lg p-6">
          <h3 className="font-heading font-semibold text-[#191D1E] mb-4">
            Architecture Importée - Validation
          </h3>

          <div className="mb-6 grid grid-cols-3 gap-4">
            <div className="p-4 bg-[#E8F4FD] rounded-lg text-center">
              <div className="text-2xl font-bold text-[#397DFF]">{architectureImportee.nb_sites}</div>
              <div className="text-xs text-[#687175]">Sites (SIRET)</div>
            </div>
            <div className="p-4 bg-[#E8F4FD] rounded-lg text-center">
              <div className="text-2xl font-bold text-[#397DFF]">{architectureImportee.nb_pdl_total}</div>
              <div className="text-xs text-[#687175]">PDL Total</div>
            </div>
            <div className="p-4 bg-[#E8F4FD] rounded-lg text-center">
              <div className="text-2xl font-bold text-[#397DFF]">
                {(architectureImportee.nb_pdl_total / architectureImportee.nb_sites).toFixed(1)}
              </div>
              <div className="text-xs text-[#687175]">PDL/Site moyen</div>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto border border-[#BFC6CC] rounded-lg p-4">
            {architectureImportee.sites.map((site, siteIdx) => (
              <div key={siteIdx} className="mb-4 last:mb-0">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-[#397DFF]" />
                  <span className="font-semibold text-[#191D1E]">
                    {site.nom_site || `Site ${siteIdx + 1}`}
                  </span>
                  <span className="font-mono text-sm text-[#687175]">SIRET: {site.siret}</span>
                  {modeCalcul === 'siret' && site.valeur_ajoutee_siret && (
                    <span className="text-sm text-[#397DFF]">
                      VAC: {parseFloat(site.valeur_ajoutee_siret).toLocaleString('fr-FR')} €
                    </span>
                  )}
                </div>
                <div className="ml-6 space-y-1">
                  {site.pdls.map((pdl, pdlIdx) => (
                    <div key={pdlIdx} className="flex items-center gap-2 text-sm text-[#687175]">
                      <MapPin className="w-3 h-3" />
                      <span className="font-mono">{pdl.pdl}</span>
                      <span>{pdl.puissance_souscrite} kVA</span>
                      <span className="font-mono">{pdl.code_naf}</span>
                      {pdl.domaine_tension_override && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium" title="Domaine de tension forcé manuellement">
                          {pdl.domaine_tension_override === 'bt_inf' && 'BT inf.'}
                          {pdl.domaine_tension_override === 'bt_sup' && 'BT sup.'}
                          {pdl.domaine_tension_override === 'hta' && 'HTA'}
                          {' '}forcé
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {modeCalcul === 'siret' && (
            <div className="mt-6 p-4 bg-[#FFF9E6] border border-[#F9C80E] rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h4 className="font-medium text-[#191D1E] mb-1">Répartition Automatique VAC</h4>
                  <p className="text-sm text-[#687175]">
                    Vous n'avez pas renseigné la VAC par site ? Entrez la VAC totale de l'entreprise,
                    elle sera répartie proportionnellement au nombre de PDL de chaque site.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={repartirVACAutomatique}
                  className="px-4 py-2 bg-[#F9C80E] hover:bg-[#E0B50D] text-[#191D1E] rounded-lg text-sm font-medium whitespace-nowrap"
                >
                  Répartir VAC
                </button>
              </div>
            </div>
          )}

          {modeCalcul === 'siret' && architectureImportee.sites.some(s => !s.valeur_ajoutee_siret || s.valeur_ajoutee_siret === 0) && (
            <div className="my-4 p-4 bg-[#F9C80E]/10 border border-[#F9C80E] rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-[#191D1E] mb-1">⚠️ VAC manquantes</div>
                  <div className="text-sm text-[#687175]">Répartissez la VAC totale sur vos sites</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRepartitionVAC(true)}
                  className="px-4 py-2 bg-[#F9C80E] hover:bg-[#E8B308] text-white rounded-lg text-sm font-medium"
                >
                  Répartir VAC
                </button>
              </div>
            </div>
          )}

          {showRepartitionVAC && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
                <h3 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
                  Répartition Valeur Ajoutée
                </h3>

                <div className="mb-6 p-4 bg-[#E8F4FD] rounded-lg">
                  <div className="text-sm text-[#687175]">VAC Totale SIREN</div>
                  <div className="text-2xl font-bold text-[#397DFF]">
                    {parseFloat(formData.valeur_ajoutee_siren).toLocaleString('fr-FR')} €
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer ${modeRepartition === 'uniforme' ? 'border-[#397DFF] bg-[#E8F4FD]' : 'border-[#BFC6CC]'}`}>
                    <input type="radio" name="repartition" value="uniforme" checked={modeRepartition === 'uniforme'} onChange={() => setModeRepartition('uniforme')} className="mt-1" />
                    <div>
                      <div className="font-medium text-[#191D1E]">Répartition Uniforme (PDL)</div>
                      <div className="text-sm text-[#687175]">Au prorata du nombre de PDL par site</div>
                    </div>
                  </label>

                  <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer ${modeRepartition === 'prorata' ? 'border-[#397DFF] bg-[#E8F4FD]' : 'border-[#BFC6CC]'}`}>
                    <input type="radio" name="repartition" value="prorata" checked={modeRepartition === 'prorata'} onChange={() => setModeRepartition('prorata')} className="mt-1" />
                    <div>
                      <div className="font-medium text-[#191D1E]">Prorata Consommation</div>
                      <div className="text-sm text-[#687175]">Selon la consommation réelle de chaque site</div>
                    </div>
                  </label>

                  <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer ${modeRepartition === 'manuel' ? 'border-[#397DFF] bg-[#E8F4FD]' : 'border-[#BFC6CC]'}`}>
                    <input type="radio" name="repartition" value="manuel" checked={modeRepartition === 'manuel'} onChange={() => setModeRepartition('manuel')} className="mt-1" />
                    <div>
                      <div className="font-medium text-[#191D1E]">Pourcentage Manuel</div>
                      <div className="text-sm text-[#687175]">Définir % pour chaque site</div>
                    </div>
                  </label>
                </div>

                {modeRepartition === 'manuel' && (
                  <div className="mb-6 border border-[#BFC6CC] rounded-lg p-4">
                    <div className="text-sm font-medium text-[#191D1E] mb-3">Pourcentages par site</div>
                    {architectureImportee.sites.map((site, idx) => (
                      <div key={idx} className="flex items-center gap-3 mb-2">
                        <div className="flex-1 text-sm text-[#687175]">
                          {site.nom_site || `Site ${idx + 1}`} ({site.siret})
                        </div>
                        <input type="number" step="0.01" value={repartitionManuelle[idx] || ''} onChange={(e) => setRepartitionManuelle({ ...repartitionManuelle, [idx]: e.target.value })} className="w-24 px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" placeholder="0" />
                        <span className="text-sm text-[#687175]">%</span>
                      </div>
                    ))}
                    <div className="mt-3 pt-3 border-t border-[#BFC6CC] text-sm">
                      Total : {Object.values(repartitionManuelle).reduce((sum, v) => sum + parseFloat(v || 0), 0).toFixed(2)}%
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button onClick={() => setShowRepartitionVAC(false)} className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">Annuler</button>
                  <button onClick={repartirVACAutomatique} className="flex-1 bg-[#397DFF] hover:bg-[#4672B6] text-white py-3 rounded-lg">Appliquer Répartition</button>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6 pt-6 border-t border-[#BFC6CC]">
            <button type="button" onClick={() => { setShowImportSites(false); setArchitectureImportee(null); }} className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg hover:bg-[#F5F6F9]">Annuler Import</button>
            <button type="button" onClick={validerArchitecture} className="flex-1 bg-[#397DFF] hover:bg-[#4672B6] text-white py-3 rounded-lg font-medium">✓ Valider Architecture</button>
          </div>
        </div>
      )}

      {/* Formulaire ajout manuel */}
      {showAddSite && !showImportSites && (
        <div className="p-6 bg-[#F5F6F9] border-2 border-dashed border-[#397DFF] rounded-lg">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#191D1E]">Nouveau Site</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddSite(false);
                  setCurrentSite({ siret: '', nom_site: '', valeur_ajoutee_siret: '', commentaire: '', pdls: [] });
                  setCurrentPDLMulti({ pdl: '', puissance_souscrite: '', code_naf: '', domaine_tension_override: '' });
                }}
                className="text-[#687175] hover:text-[#191D1E]"
              >
                Annuler
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">SIRET *</label>
                <input type="text" value={currentSite.siret} onChange={(e) => setCurrentSite({ ...currentSite, siret: e.target.value })} className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] font-mono" placeholder="14 chiffres" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Nom Site</label>
                <input type="text" value={currentSite.nom_site} onChange={(e) => setCurrentSite({ ...currentSite, nom_site: e.target.value })} className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]" placeholder="Ex: Site de production" />
              </div>
            </div>

            {modeCalcul === 'siret' && (
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Valeur Ajoutée du Site (€) *</label>
                <input type="number" value={currentSite.valeur_ajoutee_siret} onChange={(e) => setCurrentSite({ ...currentSite, valeur_ajoutee_siret: e.target.value })} className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]" placeholder="VAC propre à ce site" />
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-amber-500 text-xl">📝</span>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-amber-800 mb-2">Remarques / Commentaires pour ce site</label>
                  <textarea value={currentSite.commentaire || ''} onChange={(e) => setCurrentSite({ ...currentSite, commentaire: e.target.value })} placeholder="Ajoutez ici vos remarques, observations ou points de clarification pour ce SIRET..." className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-h-[60px] resize-y" rows={2} />
                  <p className="text-xs text-amber-600 mt-1">Ce commentaire sera inclus dans le rapport final.</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="taux-reduit-deja-applique">
                <input type="checkbox" checked={currentSite.taux_reduit_deja_applique || false} onChange={(e) => setCurrentSite({ ...currentSite, taux_reduit_deja_applique: e.target.checked })} className="w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500" />
                <div>
                  <span className="font-semibold text-blue-800 text-sm">Taux réduit TICFE déjà appliqué sur ce site</span>
                  <p className="text-xs text-blue-600 mt-0.5">Cochez si ce site bénéficie déjà du taux réduit sur ses factures. Il sera exclu du calcul des économies.</p>
                </div>
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-[#191D1E]">PDL de ce site</label>
                <div className="flex gap-2">
                  {editingPDLMultiIndex !== null && (
                    <button type="button" onClick={() => { setEditingPDLMultiIndex(null); setCurrentPDLMulti({ pdl: '', puissance_souscrite: '', code_naf: '', domaine_tension_override: '', tranches_eligibilite: [] }); }} className="text-sm text-[#687175] hover:text-[#191D1E] font-medium">Annuler</button>
                  )}
                  <button type="button" onClick={addPDLToSite} className="text-sm text-[#397DFF] hover:text-[#4672B6] font-medium">
                    {editingPDLMultiIndex !== null ? 'Valider modification' : '+ Ajouter PDL'}
                  </button>
                </div>
              </div>

              {currentSite.pdls.length > 0 && (
                <div className="mb-4 space-y-2">
                  {currentSite.pdls.map((pdl, idx) => (
                    <div key={idx} className={`flex items-center gap-3 p-2 border rounded text-sm ${editingPDLMultiIndex === idx ? 'bg-[#E8F4FD] border-[#397DFF]' : 'bg-white border-[#BFC6CC]'}`}>
                      <span className="font-mono">{pdl.pdl}</span>
                      <span className="text-[#687175]">{pdl.puissance_souscrite} kVA</span>
                      <span className="font-mono">{pdl.code_naf}</span>
                      {pdl.domaine_tension_override && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium" title="Domaine de tension forcé manuellement">
                          {{ bt_inf: 'BT inf.', bt_sup: 'BT sup.', hta: 'HTA' }[pdl.domaine_tension_override]} forcé
                        </span>
                      )}
                      <button type="button" onClick={() => { setCurrentPDLMulti({ ...pdl }); setEditingPDLMultiIndex(idx); }} className="ml-auto text-[#397DFF] hover:text-[#4672B6]" title="Modifier ce PDL"><Pencil className="w-3 h-3" /></button>
                      <button type="button" onClick={() => setCurrentSite({ ...currentSite, pdls: currentSite.pdls.filter((_, i) => i !== idx) })} className="text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input type="text" value={currentPDLMulti.pdl} onChange={(e) => setCurrentPDLMulti({ ...currentPDLMulti, pdl: e.target.value })} className="px-3 py-2 border border-[#BFC6CC] rounded-lg font-mono text-sm" placeholder="N° PDL" />
                <input type="number" value={currentPDLMulti.puissance_souscrite} onChange={(e) => setCurrentPDLMulti({ ...currentPDLMulti, puissance_souscrite: e.target.value })} className="px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" placeholder="kVA" />
                <CodeNAFAutocomplete value={currentPDLMulti.code_naf} onChange={(val) => setCurrentPDLMulti({ ...currentPDLMulti, code_naf: val })} />
                <select
                  value={currentPDLMulti.domaine_tension_override || ''}
                  onChange={(e) => setCurrentPDLMulti({ ...currentPDLMulti, domaine_tension_override: e.target.value })}
                  className="px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm bg-white"
                  data-testid="pdl-domaine-tension"
                  title="Forcer le domaine de tension. Par défaut (auto), il est déduit de la puissance souscrite."
                >
                  <option value="">Domaine tension : Auto (selon kVA)</option>
                  <option value="bt_inf">BT inférieure (&lt; 36 kVA)</option>
                  <option value="bt_sup">BT supérieure (36 → 250 kVA)</option>
                  <option value="hta">HTA / Haute tension (&gt; 250 kVA)</option>
                </select>
              </div>

              {/* Avertissement intelligent : incohérence puissance ↔ domaine choisi */}
              {(() => {
                const overr = currentPDLMulti.domaine_tension_override;
                const sugg = suggestedTensionFromKva(currentPDLMulti.puissance_souscrite);
                if (!overr || !sugg || overr === sugg) return null;
                return (
                  <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="pdl-tension-warning">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      Avec {currentPDLMulti.puissance_souscrite} kVA, le raccordement habituel est{' '}
                      <strong>{TENSION_LABEL[sugg]}</strong>. Êtes-vous sûr de vouloir forcer{' '}
                      <strong>{TENSION_LABEL[overr]}</strong> ? Cela impacte le tarif CERFA appliqué.
                    </div>
                  </div>
                );
              })()}

              <div className="mt-3 ml-1">
                <label className="block text-xs font-medium text-[#687175] mb-1">Tranches d'éligibilité</label>
                <TranchesEligibilite pdl={currentPDLMulti.pdl} codeNaf={currentPDLMulti.code_naf} tranches={currentPDLMulti.tranches_eligibilite || []} onChange={(t) => setCurrentPDLMulti({ ...currentPDLMulti, tranches_eligibilite: t })} />
              </div>
            </div>

            <button type="button" onClick={addSiteToRapport} className="w-full py-3 bg-[#397DFF] hover:bg-[#4672B6] text-white rounded-lg font-medium">
              Valider ce Site ({currentSite.pdls.length} PDL)
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between gap-3 pt-6">
        <button onClick={() => setStep(3)} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">← Retour</button>
        <div className="flex gap-3">
          <button type="button" onClick={sauvegarderBrouillon} className="px-6 py-3 border border-[#F9C80E] text-[#F9C80E] font-medium rounded-lg hover:bg-[#FFF9E6]">💾 Enregistrer Brouillon</button>
          <button onClick={() => setStep(5)} className="bg-[#397DFF] text-white py-3 px-8 rounded-lg" disabled={formData.sites.length === 0}>Suivant : Consommations →</button>
        </div>
      </div>
    </div>
  );
};

export default Step4Sites;
